import { faArrowRight, faEye, faEyeSlash, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { SERVER_URL } from '../../config';
import toast from 'react-hot-toast';
import axios from 'axios';
import GoogleTranslate from '../../GoogleTranslate';

export default function ForgetPass() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const roleFromQuery = queryParams.get("role") || "player";
    const [isLoading, setIsLoading] = useState(false);

    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [role, setRole] = useState(roleFromQuery);
    const [errors, setErrors] = useState({});

    const [resendTimer, setResendTimer] = useState(60);
    const [isResendDisabled, setIsResendDisabled] = useState(false);
    const [isResendingOTP, setIsResendingOTP] = useState(false);

    useEffect(() => {
        if (isResendDisabled) {
            const timer = setInterval(() => {
                setResendTimer((prev) => {
                    if (prev === 1) {
                        clearInterval(timer);
                        setIsResendDisabled(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [isResendDisabled]);

    useEffect(() => {
        setRole(roleFromQuery);
    }, [roleFromQuery]);

    const togglePasswordVisibility = () => setShowPassword(prev => !prev);

    const headings = {
        1: "Forget Password",
        2: "Verify OTP",
        3: "Reset Password",
    };

    const handleSendOTP = async () => {
        const newErrors = {};

        if (!email.trim()) {
            newErrors.email = "Email is required";
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email.trim())) newErrors.email = "Invalid email format";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setIsLoading(true);
        try {
            const response = await axios.post(`${SERVER_URL}/api/v2/generate-otp`, { email, role });
            if (response.data.status) {
                toast.success("OTP sent to your email!");
                setStep(2);
                setIsResendDisabled(true);
            } else {
                toast.error(response.data.msg || "Failed to send OTP.");
            }
        } catch (error) {
            toast.error("Error sending OTP. Try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        const newErrors = {};

        if (!otp.trim()) {
            newErrors.otp = "OTP is required";
        } else if (!/^\d{4,6}$/.test(otp.trim())) {
            newErrors.otp = "OTP must be 4-6 digits";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        try {
            const response = await axios.post(`${SERVER_URL}/api/v2/verify-otp`, { email, role, otp });
            if (response.data.status) {
                toast.success("OTP verified successfully!");
                setStep(3);
            } else {
                toast.error("Incorrect OTP. Try again.");
            }
        } catch (error) {
            console.error(error);
            newErrors.otp = error?.response?.data?.msg || "OTP verification failed.";
            setErrors(newErrors);
        }
    };

    const handleResetPassword = async () => {
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
        const newErrors = {};

        if (!newPassword) {
            newErrors.password = "Password is required";
        } else if (!passwordRegex.test(newPassword)) {
            newErrors.password = "Password must be at least 8 characters with uppercase, lowercase, number & special character";
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = "Please confirm your password";
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        try {
            const response = await axios.post(`${SERVER_URL}/api/v2/update-password`, { email, newPassword, role });
            if (response.data.status) {
                toast.success("Password reset successfully!");
                let path = role === "players" ? "player" : "club";
                setTimeout(() => navigate(`/${path}/login`), 1500);
            } else {
                toast.error("Failed to reset password.");
            }
        } catch (error) {
            newErrors.confirmPassword = error?.response?.data?.msg;
            setErrors(newErrors);
            // toast.error("Error resetting password. Try again.");
        }
    };

    const handleResendOTP = async () => {
        setIsResendingOTP(true);
        setIsResendDisabled(true);
        setResendTimer(60);
        await handleSendOTP();
        setIsResendingOTP(false);
    };

    return (
        <div className='lg:h-screen items-center justify-center bg-cover pt-10 lg:pt-0 overflow-hidden' style={{ backgroundImage: 'url("/assets/bg/Authbg.svg")' }}>
            <div className='flex justify-between w-full md:mb-0 mb-10'>
                <div className='w-[50%]'>
                    <button onClick={() => { window.location.href = '/' }} className="z-50 cursor-pointer absolute top-5 left-5 flex items-center gap-2 text-white px-4 py-2 rounded-full">
                        <FontAwesomeIcon icon={faArrowLeft} />
                        <span>Back</span>
                    </button>
                </div>
                <div className='top-5 right-1 absolute'>
                    <GoogleTranslate />
                </div>
            </div>

            <div className='w-[90%] flex flex-col-reverse lg:flex-row items-center gap-10'>
                <div className='w-[100%] lg:w-[50%] relative'>
                    <img src='/assets/others/herologin.svg' className='md:w-[75%] mx-auto' data-aos='fade-right' data-aos-duration='1500' />
                    <img src='/assets/others/Ball.svg' className='ball-spin w-[25%] absolute bottom-10 right-0 lg:hidden block' style={{ zIndex: 1 }} />
                </div>

                <div className='w-[100%] lg:w-[50%] text-white relative'>
                    <div className='bg-white/5 lg:w-[90%] 2xl:w-[80%] 2xl:mx-auto p-7 lg:px-10 py-10 lg:py-20 2xl:py-24' data-aos='fade-down' data-aos-duration='1500'>
                        <h1 className='text-center text-3xl md:text-5xl 2xl:text-6xl' style={{ fontFamily: 'Sacramento' }}>{headings[step]}</h1>

                        {step === 1 && (
                            <div className='mt-10'>
                                <p className='text-sm text-gray-300 mb-3'>Enter your registered email address. We will send a one-time password (OTP) to reset your password.</p>
                                <label className='font-semibold text-lg'>Email</label>
                                <input
                                    type='email'
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setEmail(value);
                                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                        setErrors((prev) => ({
                                            ...prev,
                                            email: value && !emailRegex.test(value.trim()) ? "Invalid email format" : "",
                                        }));
                                    }}
                                    className='w-full mt-1 border rounded outline-none py-2 px-3'
                                />
                                {errors.email && <p className="text-red-400 text-sm mt-2">{errors.email}</p>}

                                <div className='flex justify-center mt-10'>
                                    <button onClick={handleSendOTP} disabled={isLoading} className='border rounded-full flex items-center gap-2 py-[2px] pl-5 pr-[2px] cursor-pointer disabled:opacity-50'>
                                        <span>{isLoading ? "Sending..." : "Send OTP"}</span>
                                        <div className='w-8 h-8 rounded-full bg-white text-black grid place-content-center'>
                                            {isLoading ? (
                                                <svg className="animate-spin h-4 w-4 text-black" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                                </svg>
                                            ) : (
                                                <FontAwesomeIcon icon={faArrowRight} />
                                            )}
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className='mt-10'>
                                <p className='text-sm text-gray-300 mb-3'>Check your email for the OTP code. Enter the code below to verify your identity.</p>
                                <label className='font-semibold text-lg'>Enter OTP</label>
                                <input type='text' onChange={(e) => setOtp(e.target.value)} className='w-full mt-1 border rounded outline-none py-2 px-3' />
                                {errors.otp && <p className="text-red-400 text-sm mt-2">{errors.otp}</p>}

                                <div className='flex justify-center mt-10'>
                                    <button onClick={handleResendOTP} disabled={isResendDisabled || isResendingOTP} className={`border rounded-full mr-5 px-5 py-2 ${isResendDisabled || isResendingOTP ? "opacity-50 cursor-not-allowed" : ""}`}>
                                        {isResendingOTP ? "Resending..." : `Resend ${resendTimer > 0 ? `(${resendTimer}s)` : ""}`}
                                    </button>
                                    <button onClick={handleVerifyOTP} className='border rounded-full flex items-center gap-2 py-[2px] pl-5 pr-[2px] cursor-pointer'>
                                        <span>Verify OTP</span>
                                        <div className='w-8 h-8 rounded-full bg-white text-black grid place-content-center'>
                                            <FontAwesomeIcon icon={faArrowRight} />
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className='mt-10'>
                                <p className='text-sm text-gray-300 mb-3'>Create a strong new password to secure your account.</p>
                                <label className='font-semibold text-lg'>New Password</label>
                                <div className='relative'>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setNewPassword(value);
                                            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
                                            setErrors((prev) => ({
                                                ...prev,
                                                confirmPassword: '',
                                                password: value && !passwordRegex.test(value)
                                                    ? "Password must be at least 8 characters with uppercase, lowercase, number & special character"
                                                    : "",
                                            }));
                                        }}
                                        className='w-full mt-1 mb-2 border rounded outline-none py-2 px-3'
                                    />
                                    <button onClick={togglePasswordVisibility} className='absolute top-3 right-3'>
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className='text-gray-500' />
                                    </button>
                                    {errors.password && <p className="text-red-400 text-sm mt-1">{errors.password}</p>}
                                </div>

                                <label className='font-semibold text-lg mt-3'>Confirm Password</label>
                                <div className='relative'>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setConfirmPassword(value);
                                            setErrors((prev) => ({
                                                ...prev,
                                                confirmPassword: value && value !== newPassword
                                                    ? "Passwords do not match"
                                                    : "",
                                            }));
                                        }}
                                        className='w-full mt-1 border rounded outline-none py-2 px-3'
                                    />
                                    <button onClick={togglePasswordVisibility} className='absolute top-3 right-3'>
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className='text-gray-500' />
                                    </button>
                                    {errors.confirmPassword && <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>}
                                </div>

                                <div className='flex justify-center mt-10'>
                                    <button onClick={handleResetPassword} className='border rounded-full flex items-center gap-2 py-[2px] pl-5 pr-[2px] cursor-pointer'>
                                        <span>Reset Password</span>
                                        <div className='w-8 h-8 rounded-full bg-white text-black grid place-content-center'>
                                            <FontAwesomeIcon icon={faArrowRight} />
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
}
